import { FC } from 'react';
import { CodeDisplayBox, CustomButton, LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, Grid, Modal, Typography } from '@mui/material';
import { useGetETLJobLogInfiniteQuery } from '~/api/v2/hooks';
import { ModalHeader } from '~/components';
import { modalActionBlockStyles, modalContainerStyle, modalOverflowBoxStyles } from '~/components/styles';
import { useInfiniteScroll } from '~/hooks';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';

export interface LogsModalProps {
  payload: {
    id: string;
    projectId: number;
    jobId: string;
    jobName: string;
  };
}

const LogsModal: FC<LogsModalProps> = ({ payload: { id, projectId, jobId, jobName } }) => {
  const dispatch = useAppDispatch();
  const { data, isFetching, isError, fetchNextPage, hasNextPage } = useGetETLJobLogInfiniteQuery({ projectId, jobId });

  const { ref } = useInfiniteScroll({
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetching,
  });

  const normalizeLine = (value: string) => value.replaceAll('\\n', '\n').replaceAll('\\t', '\t');

  const parsedLines = (data?.pages.flatMap((page) => page.lines) || [])
    .flatMap((line) => normalizeLine(line || '').split('\n'))
    .map((line) => line.replace(/\r/g, ''));

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  const renderLogsContent = () => {
    if (isFetching && !parsedLines.length) return <Typography variant="body1">Loading logs...</Typography>;
    else if (isError) return <Typography color="error.main">Failed to load logs. Please try again.</Typography>;
    else if (!parsedLines.length) return <Typography variant="body1">No logs available.</Typography>;
    else
      return (
        <>
          <CodeDisplayBox darkMode code={parsedLines.join('\n')} />
          {isFetching && hasNextPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1 }}>
              <LoadingSpinner spinnerSize="xs" />
            </Box>
          )}
          {hasNextPage && <Box ref={ref} sx={{ height: '1px' }} />}
        </>
      );
  };

  return (
    <Modal open onClose={handleClose}>
      <Box tabIndex={-1} sx={modalContainerStyle}>
        <ModalHeader onClose={handleClose} title={[`Logs for ${jobName}`]} />
        <Grid container spacing={2} px={3} sx={modalOverflowBoxStyles}>
          <Grid size={12}>{renderLogsContent()}</Grid>
        </Grid>
        <Box sx={modalActionBlockStyles}>
          <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
        </Box>
      </Box>
    </Modal>
  );
};

export default LogsModal;
